import React, { useMemo, useState } from "react";
import Modal from "./Modal";
import { formatItalianNumber } from "../utils/formatItalianNumber";

const TYPE_STYLE = {
  Z: { active: "border-blue-600 bg-blue-600 text-white", row: "border-l-blue-500 bg-blue-50/70" },
  B: { active: "border-purple-600 bg-purple-600 text-white", row: "border-l-purple-500 bg-purple-50/70" },
  I: { active: "border-emerald-600 bg-emerald-600 text-white", row: "border-l-emerald-500 bg-emerald-50/70" },
};

const euro = (value) => formatItalianNumber(value, {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
const quantita = (value) => formatItalianNumber(value, { maximumFractionDigits: 2 });

export const TotaliPrestazioniModal = ({ isOpen, onClose, subtitle, rows = [] }) => {
  const availableTypes = useMemo(() => {
    const found = new Set(rows.map((row) => row.tipo).filter((tipo) => TYPE_STYLE[tipo]));
    return ["Z", "B", "I"].filter((tipo) => found.has(tipo));
  }, [rows]);
  const [disabledTypes, setDisabledTypes] = useState(new Set());
  const activeTypes = availableTypes.filter((tipo) => !disabledTypes.has(tipo));

  const totals = useMemo(() => {
    const grouped = new Map();
    rows.filter((row) => activeTypes.includes(row.tipo)).forEach((row) => {
      const code = String(row.codice || "—");
      const key = `${row.tipo}:${code}`;
      const current = grouped.get(key) || {
        key, tipo: row.tipo, codice: code, descrizione: row.descrizione || "—", quantita: 0, totale: 0,
      };
      current.quantita += Number(row.quantita ?? 1);
      current.totale += Number(row.totale || 0);
      grouped.set(key, current);
    });
    return Array.from(grouped.values()).sort((a, b) =>
      a.tipo.localeCompare(b.tipo) || a.codice.localeCompare(b.codice, "it", { numeric: true })
    );
  }, [rows, activeTypes]);
  const selectedTotal = totals.reduce((sum, row) => sum + row.totale, 0);

  const toggleType = (tipo) => setDisabledTypes((current) => {
    const next = new Set(current);
    if (next.has(tipo)) next.delete(tipo);
    else if (activeTypes.length > 1) next.add(tipo);
    return next;
  });

  return (
    <Modal isOpen={isOpen} size="compact" closeButton={onClose}
      headerText={<div className="text-center"><div>Totali per codice prestazione</div><div className="mt-0.5 text-xs font-normal text-gray-500">{subtitle}</div></div>}>
      <div className="overflow-hidden rounded-md border border-slate-200">
        <div className="grid gap-3 border-b border-slate-200 bg-slate-50 px-4 py-3 sm:grid-cols-[1fr_auto] sm:items-center">
          <div>
            <div className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-slate-500">Mostra tipologia</div>
            <div className="flex gap-2">
              {availableTypes.map((tipo) => (
                <button key={tipo} type="button" onClick={() => toggleType(tipo)}
                  className={`min-w-[42px] rounded-lg border px-3 py-2 text-sm font-semibold transition ${disabledTypes.has(tipo) ? "border-slate-200 bg-white text-slate-400" : TYPE_STYLE[tipo].active}`}>
                  {tipo}
                </button>
              ))}
            </div>
          </div>
          <div className="rounded-xl border border-blue-100 bg-white px-5 py-3 text-right shadow-sm">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Totale selezionato</div>
            <div className="mt-0.5 whitespace-nowrap text-2xl font-bold text-blue-700">{euro(selectedTotal)}&nbsp;€</div>
          </div>
        </div>
        <div className="max-h-[58vh] overflow-auto p-4">
          <table className="w-full border-collapse text-sm">
            <thead className="sticky top-0 z-10 bg-white text-xs uppercase text-gray-500"><tr className="border-b"><th className="px-3 py-2 text-left">Codice</th><th className="px-3 py-2 text-left">Descrizione</th><th className="px-3 py-2 text-right">Quantità</th><th className="px-3 py-2 text-right">Totale</th></tr></thead>
            <tbody>{totals.map((row) => <tr key={row.key} className={`border-b border-l-4 border-gray-100 ${TYPE_STYLE[row.tipo].row}`}><td className="px-3 py-2 font-semibold text-blue-700">{row.codice}</td><td className="px-3 py-2 text-gray-700">{row.descrizione}</td><td className="px-3 py-2 text-right">{row.quantita ? quantita(row.quantita) : "—"}</td><td className="whitespace-nowrap px-3 py-2 text-right font-semibold">{row.totale ? <>{euro(row.totale)}&nbsp;€</> : "—"}</td></tr>)}</tbody>
          </table>
        </div>
      </div>
    </Modal>
  );
};
